import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, readTotalCount } from '../api/client';
import { API_ITEMS_URL } from '../utils';
import { elementChoicesParam } from '../utils/elementRefusal';
import { sortThenParams } from '../utils/sortLevels';
import { AUTO_RETRY_MAX_MS, retryAfterMs } from './useCollectionItems';

/** The API's largest page (keepr-api SCHEMA.md, `GET /api/items` `limit`). */
export const MAP_PAGE_SIZE = 200;
/** What one ask puts on the map: five pages. "Show more on the map" asks the next. */
export const MAP_BATCH = 1000;
/** The most a map holds, whatever the list's size (⚑ KPR-392). */
export const MAP_MAX_ITEMS = 5000;

const STALE = Symbol('stale');

const idOf = item => `${item?._id?.$oid || item?._id || ''}`;

const waitFor = (ms, signal) => new Promise((resolve) => {
	const timer = setTimeout(() => resolve(true), ms);
	signal.addEventListener('abort', () => { clearTimeout(timer); resolve(false); }, { once: true });
});

/**
 * The items a list's MAP shows (KPR-392): the SAME question the list's feed
 * asks — collection, card filter, query (the search box's term included),
 * the person's element picks, review, linked collections, sort — read from
 * the same endpoint, `GET /api/items`, so the server's scoping (grants, card
 * grants, filtered grants, element rules, the intake quarantine) is the only
 * gate, exactly as for the list.
 *
 * The feed pages 50 at a time as the reader scrolls; a map has to hold the
 * whole question at once, so this reads pages of 200 back to back: the first
 * MAP_BATCH items, then MAP_BATCH more on `loadMore()`, never past
 * MAP_MAX_ITEMS. The list's own sort decides which come first.
 *
 *   const map = useMapItems({ collectionId, cardFilter, query, elementChoices,
 *                             review, includeLinked, sortField, sortDirection,
 *                             sortThen, enabled });
 *   map.items, map.total, map.loading, map.loadingMore, map.error,
 *   map.hasMore, map.loadMore(), map.retry(), map.questionKey,
 *   map.removeItem(id), map.replaceItem(item)
 *
 * - Each question (and each time `enabled` turns on) is a generation: its
 *   requests are aborted and its answers dropped the moment it is no longer
 *   the one asked. An equal question on a re-render asks nothing.
 * - A page that fails STOPS the read and keeps what landed: `error` is
 *   `{ kind: 'rate_limited' | 'refused' | 'failed', page, retryAfterMs? }`,
 *   and `retry()` asks for that page again and carries on to the batch's
 *   end. A 429 naming a wait of AUTO_RETRY_MAX_MS or less is waited out and
 *   asked again once, by itself (the feed's rule). A 400 is the filter's —
 *   the list's own refusal handling says it — and is not retried.
 * - `enabled: false` holds every request; the items are dropped, so a map
 *   opened again reads the list afresh rather than showing an old answer.
 *   `hold: true` holds them too but reads as waiting: the map is on screen
 *   and the list does not yet know what it means (its collection or links
 *   are still loading), so "No items match" must not show meanwhile.
 * - `refresh()` asks the same question again; `removeItem(id)` and
 *   `replaceItem(item)` follow the pane's edits, a removal moving the next
 *   page's offset one place earlier as the server's list did. Rows are kept
 *   unique by id across pages.
 */
export default function useMapItems({
	collectionId,
	cardFilter,
	query,
	elementChoices = null,
	review,
	includeLinked = false,
	sortField,
	sortDirection,
	sortThen = null,
	enabled = true,
	hold = false,
}) {
	const choicesParam = query ? elementChoicesParam(elementChoices) : undefined;
	const sortThenKey = JSON.stringify(sortThenParams(sortThen));
	const params = useMemo(() => {
		const base = {
			collection_id: collectionId,
			sort_field: sortField,
			sort_direction: sortDirection,
			// "Then by" (KPR-1636): the map and the board order as the list does.
			...JSON.parse(sortThenKey),
		};
		if (cardFilter) base.card_id = cardFilter;
		if (includeLinked) base.includeLinked = 1;
		if (query) base.q = query;
		if (choicesParam) base.elementChoices = choicesParam;
		if (review) base.review = review;
		return base;
	}, [collectionId, cardFilter, includeLinked, query, choicesParam, review, sortField, sortDirection, sortThenKey]);
	const questionKey = useMemo(() => JSON.stringify(Object.keys(params).sort().map(key => [key, params[key]])), [params]);

	const [items, setItems] = useState([]);
	// The items as the read sees them, so a page appended or an item removed
	// is worked out once, outside React's updaters (which may run twice).
	const itemsRef = useRef([]);
	const commitItems = useCallback((next) => {
		itemsRef.current = next;
		setItems(next);
	}, []);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(Boolean(enabled));
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState(null);
	// The question `items` answer: nothing of another question is ever drawn.
	const [answeredKey, setAnsweredKey] = useState(null);
	// `refresh()` asks the same question again (an intake item decided in the pane).
	const [reloads, setReloads] = useState(0);

	// The run in progress: its generation, its question, its abort, the server
	// offset the next page starts at, how many items it means to hold before
	// it stops, the server's total, and the offset of a page still owed.
	const runRef = useRef({ generation: 0, key: null, params: null, controller: null, next: 0, target: 0, total: null, busy: false, failedSkip: null });

	// One page; resolves with the page's rows, STALE, or { error }.
	const fetchPage = useCallback((skip, run, generation) => {
		// The run's own question, fixed when it started: a page can never be
		// asked with, or filed under, a question that came after it.
		const request = { ...run.params, limit: MAP_PAGE_SIZE, skip };
		return api.get(API_ITEMS_URL, { params: request, signal: run.controller.signal })
			.then(({ data, headers }) => {
				if (generation !== runRef.current.generation) return STALE;
				const rows = Array.isArray(data) ? data : [];
				return { rows, total: readTotalCount(headers, skip + rows.length) };
			})
			.catch((err) => (generation !== runRef.current.generation ? STALE : { error: err }));
	}, []);

	// Read pages until the run holds its target, the list ends, or a page fails.
	const pump = useCallback(async (generation) => {
		const run = runRef.current;
		let retried = false;
		while (generation === runRef.current.generation) {
			if (itemsRef.current.length >= run.target) break;
			if (run.total !== null && run.next >= run.total) break;
			const skip = run.next;
			// eslint-disable-next-line no-await-in-loop
			const result = await fetchPage(skip, run, generation);
			if (result === STALE) return;
			if (result.error) {
				const status = result.error?.response?.status;
				const wait = status === 429 ? retryAfterMs(result.error) : null;
				if (status === 429 && !retried && wait !== null && wait <= AUTO_RETRY_MAX_MS) {
					retried = true;
					// eslint-disable-next-line no-await-in-loop
					const go = await waitFor(wait, run.controller.signal);
					if (!go || generation !== runRef.current.generation) return;
					continue; // the same page, once more
				}
				run.failedSkip = skip;
				setError({
					kind: status === 429 ? 'rate_limited' : status === 400 ? 'refused' : 'failed',
					page: Math.floor(skip / MAP_PAGE_SIZE),
					...(wait !== null ? { retryAfterMs: wait } : {}),
				});
				break;
			}
			retried = false;
			const { rows } = result;
			run.total = result.total;
			run.next = skip + rows.length;
			// An item that moved across a page boundary since the last page (one
			// added by someone else) is not listed twice.
			const held = skip === 0 ? [] : itemsRef.current;
			const seen = new Set(held.map(idOf));
			commitItems([...held, ...rows.filter(row => !seen.has(idOf(row)))]);
			setTotal(result.total);
			setAnsweredKey(run.key);
			if (rows.length < MAP_PAGE_SIZE) {
				// A short page is the list's end, whatever the count said.
				run.total = run.next;
				setTotal(run.next);
				break;
			}
		}
		if (generation !== runRef.current.generation) return;
		run.busy = false;
		setLoading(false);
		setLoadingMore(false);
	}, [fetchPage, commitItems]);

	// A new question (or the map opened, or a refresh): start over.
	useEffect(() => {
		const previous = runRef.current;
		previous.controller?.abort();
		const generation = previous.generation + 1;
		runRef.current = {
			generation,
			key: questionKey,
			params,
			controller: new AbortController(),
			next: 0,
			target: MAP_BATCH,
			total: null,
			busy: false,
			failedSkip: null,
		};
		commitItems([]);
		setTotal(0);
		setError(null);
		setLoadingMore(false);
		setAnsweredKey(null);
		if (!enabled || hold || !params.collection_id) {
			setLoading(false);
			return undefined;
		}
		setLoading(true);
		runRef.current.busy = true;
		pump(generation);
		return undefined;
	}, [questionKey, enabled, hold, reloads, pump, commitItems]); // eslint-disable-line react-hooks/exhaustive-deps

	// Unmounted: every answer still on its way is stale.
	useEffect(() => () => {
		runRef.current.controller?.abort();
		runRef.current.generation += 1;
	}, []);

	const loadMore = useCallback(() => {
		const run = runRef.current;
		if (!enabled || hold || run.busy || run.failedSkip !== null) return;
		if (run.total !== null && run.next >= run.total) return;
		const held = itemsRef.current.length;
		if (held >= MAP_MAX_ITEMS) return;
		run.target = Math.min(MAP_MAX_ITEMS, held + MAP_BATCH);
		run.busy = true;
		setLoadingMore(true);
		pump(run.generation);
	}, [enabled, hold, pump]);

	const retry = useCallback(() => {
		const run = runRef.current;
		if (!enabled || hold || run.busy || run.failedSkip === null) return;
		run.next = run.failedSkip;
		run.failedSkip = null;
		run.busy = true;
		setError(null);
		if (!itemsRef.current.length) setLoading(true);
		else setLoadingMore(true);
		pump(run.generation);
	}, [enabled, hold, pump]);

	const refresh = useCallback(() => setReloads(count => count + 1), []);

	// The pane deleted an item, or changed one (its place, say): the map
	// follows without asking again. A deleted item also moves every later
	// one up a place on the server, so the next page starts one earlier.
	const removeItem = useCallback((itemId) => {
		const id = `${itemId}`;
		const held = itemsRef.current;
		const next = held.filter(item => idOf(item) !== id);
		if (next.length === held.length) return;
		const run = runRef.current;
		run.next = Math.max(0, run.next - 1);
		if (run.total !== null) run.total = Math.max(0, run.total - 1);
		commitItems(next);
		setTotal(count => Math.max(0, count - 1));
	}, [commitItems]);
	const replaceItem = useCallback((updated) => {
		const id = idOf(updated);
		if (!id) return;
		const held = itemsRef.current;
		const index = held.findIndex(item => idOf(item) === id);
		if (index === -1) return;
		const next = [...held];
		next[index] = updated;
		commitItems(next);
	}, [commitItems]);

	const current = answeredKey === questionKey;
	const shown = current ? items : [];
	const shownTotal = current ? total : 0;
	const run = runRef.current;
	// Waiting, as the reader sees it: a read under way, a read held until
	// the list knows what it means, or a question asked and not yet answered
	// (the moment between a new question and the read it starts).
	const waiting = Boolean(enabled) && !error && (loading || hold || !current);
	const hasMore = current && !waiting && !loadingMore && run.failedSkip === null
		&& shown.length < shownTotal && shown.length < MAP_MAX_ITEMS;

	return {
		items: shown,
		total: shownTotal,
		loading: waiting,
		loadingMore,
		error,
		hasMore,
		capped: current && shownTotal > MAP_MAX_ITEMS,
		loadMore,
		retry,
		refresh,
		removeItem,
		replaceItem,
		questionKey,
	};
}
